import { ShieldCheck, Users, Clock, MapPin, Heart, BadgeCheck } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { useScrollReveal } from "@/hooks/useScrollReveal";
import therapyFamilyImg from "@/assets/landing/therapy-family.webp";
import heroBgImg from "@/assets/landing/hero-bg.webp";
import familyImpactImg from "@/assets/landing/family-impact.webp";
import coupleTherapyImg from "@/assets/landing/couple-therapy.webp";

const items = [
  {
    icon: ShieldCheck,
    title: "Clínicas Verificadas e Seguras",
    desc: "Todas as clínicas passam por verificação de documentação, licenças e alvará de funcionamento.",
  },
  {
    icon: Users,
    title: "Equipe Multidisciplinar",
    desc: "Psiquiatras, psicólogos, enfermeiros, terapeutas ocupacionais e assistentes sociais.",
  },
  {
    icon: Clock,
    title: "Atendimento 24 Horas",
    desc: "Orientação e suporte disponíveis a qualquer momento para emergências e dúvidas.",
  },
  {
    icon: MapPin,
    title: "Cobertura Nacional",
    desc: "Clínicas em todos os estados do Brasil, com busca inteligente por proximidade.",
  },
  {
    icon: Heart,
    title: "Tratamento Humanizado",
    desc: "Abordagem acolhedora que respeita a individualidade de cada paciente e sua família.",
  },
  {
    icon: BadgeCheck,
    title: "Alta Taxa de Recuperação",
    desc: "Clínicas com programas comprovados de prevenção de recaídas e acompanhamento pós-alta.",
  },
];

const galleryImages = [
  { src: therapyFamilyImg, alt: "Terapia familiar em clínica de recuperação" },
  { src: heroBgImg, alt: "Superando a dependência - ajuda profissional" },
  { src: familyImpactImg, alt: "Impacto da dependência na família" },
  { src: coupleTherapyImg, alt: "Casal em terapia - caminho para a recuperação" },
];

export default function LandingDifferentials() {
  const galleryRef = useScrollReveal<HTMLDivElement>();

  return (
    <section className="bg-muted/20 border-y">
      <div className="max-w-5xl mx-auto px-4 py-12">
        <h2 className="text-2xl font-bold text-foreground text-center mb-2">
          Diferenciais do Busca Clínicas
        </h2>
        <p className="text-muted-foreground text-center text-sm mb-8">
          Por que milhares de famílias confiam na nossa plataforma
        </p>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-12">
          {items.map((item) => (
            <Card
              key={item.title}
              className="border-border/50 bg-card/80 hover:shadow-md hover:-translate-y-1 transition-all duration-300"
            >
              <CardContent className="p-5 flex gap-4 items-start">
                <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
                  <item.icon className="h-5 w-5 text-primary" />
                </div>
                <div>
                  <h3 className="text-sm font-semibold text-foreground mb-1">{item.title}</h3>
                  <p className="text-xs text-muted-foreground leading-relaxed">{item.desc}</p>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        <div ref={galleryRef} className="scroll-reveal">
          <h3 className="text-xl font-bold text-foreground text-center mb-2">
            Conheça Nossa Estrutura
          </h3>
          <p className="text-muted-foreground text-center text-sm mb-6">
            Estrutura moderna e completa, projetada para oferecer o melhor atendimento
          </p>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {galleryImages.map((img, i) => (
              <div
                key={i}
                className="rounded-xl overflow-hidden border border-border/50 shadow-sm hover:shadow-md transition-shadow"
              >
                <img
                  src={img.src}
                  alt={img.alt}
                  className="w-full aspect-[3/4] object-cover hover:scale-105 transition-transform duration-500"
                  loading="lazy"
                  decoding="async"
                  width={280}
                  height={373}
                />
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
